import type { ProgrammaticLandingPageConfig } from '../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from './storageSizeFacts'

export const KWH_10_SPEICHER_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '10-kwh-speicher-kosten',
  lang: 'de',
  topicId: 'kwh-speicher-10',
  title: '10 kWh Batteriespeicher: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet ein 10-kWh-Batteriespeicher? Für wen sich diese Speichergröße eignet, ' +
    'Eigenverbrauchs-Richtwerte und kostenloser Preisvergleich für Anlage + Speicher.',
  keywords: [
    '10 kWh Speicher Kosten',
    'Batteriespeicher 10 kWh Preis',
    '10 kWh Solarspeicher',
    'Was kostet ein 10 kWh Speicher',
  ],
  eyebrow: 'Speichergröße 10 kWh',
  h1: '10 kWh Batteriespeicher: Kosten und Richtwerte',
  intro:
    `Ein 10-kWh-Speicher ist die häufigste Kombination mit einer ${STORAGE_REFERENCE_KWP}-kWp-` +
    'Anlage - beide Größen folgen der in der Praxis verbreiteten 1:1-Faustregel kWp:kWh. Hier ' +
    'finden Sie Richtwerte zur passenden Haushaltsgröße, zum Eigenverbrauch mit Speicher und ' +
    'direkt darunter einen Rechner mit dem für diese Kombination vorbelegten Durchschnittspreis ' +
    'aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 10 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich ein 10-kWh-Speicher?' },
    {
      type: 'paragraph',
      text: `Richtwert: 10 kWh gelten als Standardkombination mit einer ${STORAGE_REFERENCE_KWP}-` +
        'kWp-Anlage (verbreitete 1:1-Faustregel kWp:kWh). Bei Wärmepumpe oder ' +
        'regelmäßigem E-Auto-Laden lohnt sich zusätzlich ein Blick auf 15 oder 20 kWh.',
    },
    { type: 'heading', text: 'Eigenverbrauch mit Batteriespeicher' },
    {
      type: 'paragraph',
      text:
        `Ohne Speicher liegt der Eigenverbrauchsanteil bei einer ${STORAGE_REFERENCE_KWP}-kWp-Anlage ` +
        `typischerweise bei rund ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Mit einem passend ` +
        `dimensionierten 10-kWh-Speicher steigt dieser Anteil im Rechenmodell um ` +
        `${SELF_CONSUMPTION_BATTERY_BONUS * 100} Prozentpunkte auf rund ` +
        `${SELF_CONSUMPTION_WITH_BATTERY * 100} % - mit zusätzlicher Wärmepumpe oder E-Auto bis zu ` +
        'einer Obergrenze von 85 %. Eine genaue, auf Ihren Verbrauch zugeschnittene Berechnung ' +
        'liefert der PV-Rechner.',
    },
    { type: 'heading', text: 'Notstromfähigkeit' },
    {
      type: 'paragraph',
      text:
        'Ein 10-kWh-Speicher bietet mit einer Notstrom-Backup-Box in der Regel genug Kapazität, um ' +
        'zentrale Verbraucher über mehrere Stunden bis hin zu einem ganzen Tag zu versorgen - wie ' +
        'lange genau, hängt vom tatsächlichen Verbrauch der angeschlossenen Geräte ab. Ob und in ' +
        'welchem Umfang ein Angebot Notstromfähigkeit einschließt, ist je Handwerker ' +
        'unterschiedlich - gezielt beim Angebot nachfragen.',
    },
    { type: 'heading', text: 'Speicherkosten und Förderung' },
    {
      type: 'paragraph',
      text:
        'Speicherkosten schwanken je nach Hersteller und Installationsaufwand stark - Marktrecherche ' +
        `2026 zeigt eine Spanne von etwa ${BATTERY_COST_PER_KWH_RANGE_EUR.min} bis ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('de-DE')} € pro kWh. Speicherspezifische ` +
        'Förderungen gibt es regional unterschiedlich (z. B. Berliner Landesprogramm mit Zuschuss ' +
        'für Batteriespeicher) - Details im Förder-Überblick, eine genaue Berechnung für Ihre ' +
        'Kombination liefert der Rechner unten.',
    },
  ],
  faq: [
    {
      q: 'Warum ist 10 kWh die häufigste Kombination mit einer 10-kWp-Anlage?',
      a: 'Weil beide Größen der in der Solarbranche verbreiteten Faustregel eines 1:1-Verhältnisses kWp:kWh folgen - ein guter Startpunkt, aber kein starres Muster. Eine genaue Empfehlung für Ihren Verbrauch liefert der PV-Rechner.',
    },
    {
      q: 'Reicht 10 kWh für ein Einfamilienhaus ohne Wärmepumpe oder E-Auto?',
      a: 'In den meisten Fällen ja - 10 kWh gilt als Standardgröße für Einfamilienhäuser mit durchschnittlichem Verbrauch. Mit Wärmepumpe oder E-Auto lohnt sich ein Blick auf 15 oder 20 kWh.',
    },
    {
      q: 'Muss ein 10-kWh-Speicher immer mit einer 10-kWp-Anlage kombiniert werden?',
      a: `Nein, ${STORAGE_REFERENCE_KWP} kWp ist hier nur die Referenzgröße für die Preisdarstellung. Bei anderer Anlagengröße liefert der PV-Rechner eine passende, dachspezifische Empfehlung.`,
    },
    {
      q: 'Reicht 10 kWh, um über Nacht komplett autark zu sein?',
      a: 'In vielen Fällen ja, auch mit etwas höherem Verbrauch als beim Durchschnittshaushalt. Eine genaue, auf Ihren tatsächlichen Verbrauch zugeschnittene Einschätzung liefert der PV-Rechner.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '7 kWh Batteriespeicher: Kosten & Richtwerte', href: '/7-kwh-speicher-kosten' },
    { label: '15 kWh Batteriespeicher: Kosten & Richtwerte', href: '/15-kwh-speicher-kosten' },
    { label: '10 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/10-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
